import type { ApolloCache } from '@apollo/client';
import {
  useApolloClient,
  useMutation as useApolloMutation,
} from '@apollo/client';
import {
  useMutation as useTanstackMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useContext } from 'react';
import type { ChangeRequestConfirmContextValue } from 'src/providers/change-request-confirm-context';
import { ChangeRequestConfirmContext } from 'src/providers/change-request-confirm-context';
import { useTRPC } from 'src/utils/trpc';

import { withChangeRequestConfirm } from '@/hooks/mutations/_with-change-request-confirm';
import { invalidateChangeRequestFeeds } from '@/hooks/mutations/invalidateChangeRequestFeeds';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type {
  MutationHookConfig,
  MutationHookFactoryResult,
  MutationHookResult,
} from './types';

/**
 * Factory that creates a GraphQL-to-tRPC mutation hook — the mutation
 * counterpart of `createQueryHook`. It collapses the previous two-file pattern
 * (`useXxx.tsx` wrapper + `useXxxTRPC.tsx`) into a single declarative config.
 *
 * The returned hook:
 * - switches between the Apollo mutation and the tRPC mutation on the `trpc` flag,
 * - preserves the per-entity method name (so consumers are untouched),
 * - keeps cache-invalidation parity (Apollo cache eviction ↔ tanstack `invalidateQueries`).
 *
 * Change-request behaviour is opt-in (`isChangeRequestGated` /
 * `refreshChangeRequestFeedsWhen`). The factory picks a **governed** or a
 * **plain** hook body at creation time, so a plain mutation never reads the
 * change-request confirm context at render — only governed hooks depend on it.
 *
 * It deliberately does NOT add UI side-effects (toasts) or pass
 * `throwOnError` — `FormContext` owns mutation UX (see packages/web/CLAUDE.md).
 */
export const createMutationHook = <
  TInput,
  TTrpcInput,
  TTrpcResult,
  TGraphQLOutput,
  TGraphQLData,
  TName extends string,
  TOptions = void,
>(
  config: MutationHookConfig<
    TInput,
    TTrpcInput,
    TTrpcResult,
    TGraphQLOutput,
    TGraphQLData,
    TName,
    TOptions
  >
): MutationHookFactoryResult<TName, TInput, TGraphQLOutput, TOptions> => {
  const {
    name,
    mutationOptions,
    mapInput,
    mapResponse,
    invalidate,
    invalidateOnGraphql,
    isChangeRequestGated,
    refreshChangeRequestFeedsWhen,
    graphqlDocument,
    graphqlVariables,
    validateGraphqlResponse,
    mapGraphqlResponse,
    evictFields,
    refetchQueries,
    awaitRefetchQueries,
    apolloContext,
  } = config;

  // Per-render setup shared by the governed and plain hook bodies.
  const useMutationCore = () => {
    const trpcEnabled = useIsFeatureFlagEnabled('trpc');
    const trpc = useTRPC();
    const queryClient = useQueryClient();

    const apolloClient = useApolloClient();

    // Evict every field, then a single gc() pass — cheaper than the per-field
    // gc() the shared evictField helper does (RSP-6326 review).
    const evictApolloFields = (cache: ApolloCache<unknown>) => {
      if (!evictFields) {
        return;
      }
      evictFields.forEach((fieldName) =>
        cache.evict({ id: 'ROOT_QUERY', fieldName })
      );
      cache.gc();
    };

    // GraphQL mutation (flag off)
    const [graphqlMutation, graphqlState] = useApolloMutation(graphqlDocument, {
      update: evictFields ? evictApolloFields : undefined,
      refetchQueries,
      awaitRefetchQueries,
    });

    // tRPC mutation (flag on)
    const onSuccess = async (result: TTrpcResult): Promise<void> => {
      if (refreshChangeRequestFeedsWhen?.(result)) {
        await invalidateChangeRequestFeeds(queryClient, trpc);
      }
      for (const queryKey of invalidate?.(trpc) ?? []) {
        await queryClient.invalidateQueries({ queryKey });
      }
      // `evictFields` applies on the tRPC path too, not just the Apollo one
      // (ENG-616). `invalidate` only reaches tanstack, so while a read is still
      // UNMIGRATED and cache-first on Apollo, a write via tRPC would otherwise
      // leave its list or counter showing pre-write data — that is the stale
      // oversight `(0)` count. Evicting a field the migrated reads no longer use
      // is a no-op, so this is safe to apply unconditionally and can simply be
      // deleted from a config once that entity's reads have migrated.
      evictApolloFields(apolloClient.cache);
    };
    const trpcMutation = useTanstackMutation(mutationOptions(trpc, onSuccess));

    return {
      trpcEnabled,
      graphqlMutation,
      graphqlState,
      trpcMutation,
      trpc,
      queryClient,
    };
  };

  // Assembles the public `{ [name], loading, error }` from the per-render core
  // plus the resolved change-request confirm context (null for plain hooks).
  // Not a hook — pure assembly — so both variants share the run() logic.
  const buildResult = (
    core: ReturnType<typeof useMutationCore>,
    changeRequestConfirm: ChangeRequestConfirmContextValue | null
  ): MutationHookResult<TName, TInput, TGraphQLOutput, TOptions> => {
    const {
      trpcEnabled,
      graphqlMutation,
      graphqlState,
      trpcMutation,
      trpc,
      queryClient,
    } = core;

    const run = async (
      input: TInput,
      options?: TOptions
    ): Promise<TGraphQLOutput> => {
      if (trpcEnabled) {
        const trpcInput = mapInput(input);

        let result: TTrpcResult;
        if (isChangeRequestGated) {
          if (!changeRequestConfirm) {
            throw new Error(
              `${name}: change-request-gated mutation requires ChangeRequestConfirmProvider`
            );
          }
          result = await withChangeRequestConfirm({
            input: trpcInput,
            run: (variables) => trpcMutation.mutateAsync(variables),
            confirm: changeRequestConfirm,
          });
        } else {
          result = await trpcMutation.mutateAsync(trpcInput);
        }

        return mapResponse(result, input);
      }

      const graphqlResult = await graphqlMutation({
        variables: graphqlVariables(input, options),
        context: apolloContext?.(options),
      });
      if (!graphqlResult.data) {
        throw new Error(`Failed to run ${name}`);
      }
      validateGraphqlResponse?.(graphqlResult.data);
      // Invalidate only after the response is confirmed present and valid — a
      // no-data result or a throwing `validateGraphqlResponse` means the
      // mutation failed, so its read caches must not be invalidated.
      for (const queryKey of invalidateOnGraphql?.(trpc) ?? []) {
        await queryClient.invalidateQueries({ queryKey });
      }

      // Without a mapper the document shape IS the consumer shape (the common
      // case); the cast is the type-level assertion of that invariant. With a
      // mapper the two branches converge onto TGraphQLOutput explicitly.
      return mapGraphqlResponse
        ? mapGraphqlResponse(graphqlResult.data, input)
        : (graphqlResult.data as unknown as TGraphQLOutput);
    };

    // The mapped return type names the mutate fn per-entity (e.g. `updateAction`);
    // a computed-key object literal can't be inferred as that mapped type.
    return {
      [name]: run,
      loading: trpcEnabled ? trpcMutation.isPending : graphqlState.loading,
      error: trpcEnabled ? trpcMutation.error : (graphqlState.error ?? null),
    } as MutationHookResult<TName, TInput, TGraphQLOutput, TOptions>;
  };

  // Governed hooks read the change-request confirm context; plain hooks never
  // touch it. The choice is fixed at factory-creation time, so each returned
  // hook has a stable hook-call order.
  const useGovernedMutationHook = () =>
    buildResult(useMutationCore(), useContext(ChangeRequestConfirmContext));
  const usePlainMutationHook = () => buildResult(useMutationCore(), null);

  return isChangeRequestGated ? useGovernedMutationHook : usePlainMutationHook;
};
